/**
 * @component ry-reveal
 * @category  display
 * @summary   Hover/focus reveal — swaps between two slots when the user hovers or focuses (Fomantic UI `.ui.reveal` equivalent). Author two children with `data-visible` and `data-hidden`; the wrapper animates between them via `[effect]` (`fade`, `move`, `move-up`, `rotate`). Mostly CSS — JS only normalizes children + applies focus handling so keyboard users see the reveal too.
 *
 * Reference: Fomantic UI's reveal element. The interaction model is simple
 * (mouseenter / focus → show hidden, mouseleave / blur → show visible) and
 * lives entirely in CSS state. JS exists to enforce two slots and apply
 * accessible focus.
 *
 * @attribute effect    'fade' | 'move' | 'move-up' | 'rotate' ?   default="fade"   Transition style applied between visible and hidden slots. Pure CSS hook.
 * @attribute instant   boolean   default=false   Skip the transition; swap immediately on hover/focus. Useful when content underneath needs to be readable instantly.
 *
 * @property  revealed  boolean   Read-only — true when the hidden slot is currently shown (data-ry-state="revealed").
 *
 * @event ry:beforereveal
 *        detail: {}
 *        fires:  on mouseenter / focusin, after the already-revealed guard, before `data-ry-state` is set
 *        cancelable: preventDefault() keeps the visible slot showing; `ry:reveal` does not fire
 *
 * @event ry:reveal
 *        detail: {}
 *        fires:  when the wrapper transitions from "visible" to "revealed" (mouseenter or focusin).
 *
 * @event ry:beforeconceal
 *        detail: {}
 *        fires:  on mouseleave / focusout, after the not-revealed guard, before `data-ry-state` is cleared
 *        cancelable: preventDefault() keeps the hidden slot showing; `ry:conceal` does not fire
 *
 * @event ry:conceal
 *        detail: {}
 *        fires:  when the wrapper transitions from "revealed" back to "visible" (mouseleave or focusout).
 *
 * @keyboard  Focus management — the reveal's `tabindex="0"` makes it keyboard-focusable. Tab in to trigger reveal, Tab out to conceal.
 *
 * @state     `data-ry-state`: `"revealed"` when the hidden slot is shown; otherwise unset.
 *
 * @content   Exactly two children. Each should carry `data-visible` (the default-state child) and `data-hidden` (the reveal-state child). Order doesn't matter — ry-reveal finds them by attribute. Children without either attribute are left in DOM but ignored by the swap.
 *
 * @dom
 *   ry-reveal[effect?][instant?][data-ry-state?]   tabindex="0"   role="group"
 *     > {visible child}    data-visible   data-ry-target="visible"
 *     > {hidden child}     data-hidden    data-ry-target="hidden"
 *
 * @gotcha  Slot detection uses the `data-visible` / `data-hidden` attributes (NOT class names). If you author with Fomantic-style `.visible.content` / `.hidden.content` classes, they will NOT be detected — add the data-attribute markers.
 *
 * @gotcha  `<ry-reveal>` is keyboard-focusable by default (`tabindex="0"`). Don't nest interactive elements inside that have their own tabindex; focus order will jump unpredictably. For purely visual reveals (no useful keyboard action), set `tabindex="-1"` on the host to remove from tab order.
 *
 * @gotcha  Only TWO slots are recognized — the first `[data-visible]` and the first `[data-hidden]`. Any additional siblings are kept but not animated.
 *
 * @gotcha  `[instant]` only affects CSS transition time. JS still fires `ry:reveal` / `ry:conceal` at the same lifecycle points.
 *
 * @example basic-fade
 * ```html
 * <ry-reveal>
 *   <img data-visible src="/cover.jpg" alt="">
 *   <div data-hidden>Now revealing!</div>
 * </ry-reveal>
 * ```
 *
 * @example move-effect
 * ```html
 * <ry-reveal effect="move">
 *   <div data-visible>Hover me</div>
 *   <div data-hidden>Boo!</div>
 * </ry-reveal>
 * ```
 *
 * @example move-up
 * ```html
 * <ry-reveal effect="move-up">
 *   <img data-visible src="/normal.jpg" alt="">
 *   <img data-hidden src="/alternate.jpg" alt="">
 * </ry-reveal>
 * ```
 *
 * @example rotate
 * ```html
 * <ry-reveal effect="rotate">
 *   <div data-visible>Click to flip</div>
 *   <div data-hidden>Other side</div>
 * </ry-reveal>
 * ```
 *
 * @example instant
 * ```html
 * <ry-reveal instant>
 *   <span data-visible>Default</span>
 *   <span data-hidden>Instant swap</span>
 * </ry-reveal>
 * ```
 *
 * @tutorial mount-tags-slots
 * ```js
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * const visible = el.querySelector('[data-ry-target="visible"]');
 * const hidden = el.querySelector('[data-ry-target="hidden"]');
 * await expectEqual(visible.textContent, 'A', 'visible slot tagged');
 * await expectEqual(hidden.textContent, 'B', 'hidden slot tagged');
 * ```
 *
 * @tutorial accepts-either-child-order
 * ```js
 * // Wrong assumption: visible must come first. Order is decoupled — the
 * // wrapper finds slots by attribute, not by index.
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-hidden>B</span>
 *     <span data-visible>A</span>
 *   </ry-reveal>
 * `);
 * const visible = el.querySelector('[data-ry-target="visible"]');
 * const hidden = el.querySelector('[data-ry-target="hidden"]');
 * await expectEqual(visible.textContent, 'A');
 * await expectEqual(hidden.textContent, 'B');
 * ```
 *
 * @tutorial host-keyboard-focusable
 * ```js
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * await expectAttr(el, 'tabindex', '0', 'tabindex=0 so keyboard users can focus');
 * await expectAttr(el, 'role', 'group');
 * ```
 *
 * @tutorial preserves-explicit-tabindex
 * ```js
 * const el = mount(`
 *   <ry-reveal tabindex="-1">
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * await expectAttr(el, 'tabindex', '-1', 'explicit tabindex survives');
 * ```
 *
 * @tutorial mouseenter-fires-ry-reveal-and-sets-state
 * ```js
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * const fired = [];
 * el.addEventListener('ry:reveal', () => fired.push(1));
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * await expectEqual(el.getAttribute('data-ry-state'), 'revealed');
 * await expectEqual(el.revealed, true);
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial mouseleave-fires-ry-conceal-and-clears-state
 * ```js
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * const fired = [];
 * el.addEventListener('ry:conceal', () => fired.push(1));
 * el.dispatchEvent(new Event('mouseleave', { bubbles: true }));
 * await expectEqual(el.dataset.ryState, undefined, 'state cleared');
 * await expectEqual(el.revealed, false);
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial focus-and-blur-mirror-hover
 * ```js
 * // Keyboard users get the same reveal — focusin reveals, focusout conceals.
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * el.dispatchEvent(new Event('focusin', { bubbles: true }));
 * await expectAttr(el, 'data-ry-state', 'revealed');
 * el.dispatchEvent(new Event('focusout', { bubbles: true }));
 * await expectEqual(el.dataset.ryState, undefined);
 * ```
 *
 * @tutorial reveal-is-idempotent
 * ```js
 * // Wrong assumption: double mouseenter fires ry:reveal twice. It does NOT —
 * // the wrapper guards against duplicate transitions.
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * const fired = [];
 * el.addEventListener('ry:reveal', () => fired.push(1));
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * await expectEqual(fired.length, 1, 'ry:reveal fired only once');
 * ```
 *
 * @tutorial beforereveal-veto
 * ```js
 * // preventDefault() on ry:beforereveal keeps the visible slot showing.
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * const revealed = [];
 * el.addEventListener('ry:reveal', () => revealed.push(1));
 * el.addEventListener('ry:beforereveal', (e) => e.preventDefault(), { once: true });
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * await expectEqual(el.revealed, false, 'vetoed reveal leaves it concealed');
 * await expectEqual(revealed.length, 0, 'ry:reveal did not fire');
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * await expectEqual(el.revealed, true, 'without the veto, reveal works');
 * await expectEqual(revealed.length, 1, 'ry:reveal fired once');
 * ```
 *
 * @tutorial beforeconceal-veto
 * ```js
 * // preventDefault() on ry:beforeconceal pins the hidden slot open.
 * const el = mount(`
 *   <ry-reveal>
 *     <span data-visible>A</span>
 *     <span data-hidden>B</span>
 *   </ry-reveal>
 * `);
 * el.dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * const concealed = [];
 * el.addEventListener('ry:conceal', () => concealed.push(1));
 * el.addEventListener('ry:beforeconceal', (e) => e.preventDefault(), { once: true });
 * el.dispatchEvent(new Event('mouseleave', { bubbles: true }));
 * await expectEqual(el.revealed, true, 'vetoed conceal leaves it revealed');
 * await expectEqual(concealed.length, 0, 'ry:conceal did not fire');
 * el.dispatchEvent(new Event('mouseleave', { bubbles: true }));
 * await expectEqual(el.revealed, false, 'without the veto, conceal works');
 * await expectEqual(concealed.length, 1, 'ry:conceal fired once');
 * ```
 *
 * @see ry-image
 * @see ry-tooltip
 */

import { RyElement } from '../core/ry-element.js';

export class RyReveal extends RyElement {
  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'group');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    this.#tagSlots();
    this.on(this, 'mouseenter', this.#reveal);
    this.on(this, 'mouseleave', this.#conceal);
    this.on(this, 'focusin', this.#reveal);
    this.on(this, 'focusout', this.#conceal);
  }

  get revealed(): boolean {
    return this.dataset.ryState === 'revealed';
  }

  #tagSlots(): void {
    const visible = this.querySelector('[data-visible]') as HTMLElement | null;
    const hidden = this.querySelector('[data-hidden]') as HTMLElement | null;
    if (visible && !visible.hasAttribute('data-ry-target')) {
      visible.setAttribute('data-ry-target', 'visible');
    }
    if (hidden && !hidden.hasAttribute('data-ry-target')) {
      hidden.setAttribute('data-ry-target', 'hidden');
    }
  }

  #reveal = (): void => {
    if (this.dataset.ryState === 'revealed') return;
    if (!this.emit('beforereveal')) return;
    this.dataset.ryState = 'revealed';
    this.emit('reveal');
  };

  #conceal = (): void => {
    if (this.dataset.ryState !== 'revealed') return;
    if (!this.emit('beforeconceal')) return;
    delete this.dataset.ryState;
    this.emit('conceal');
  };
}

customElements.define('ry-reveal', RyReveal);
